/**
 * UserCreateDialog — react-hook-form + zod, mirroring backend ``UserCreate``.
 *
 * BUG-022: zod constraints (min/max/regex) match the backend schema; HTML
 * ``required`` / ``minLength`` / ``maxLength`` / ``pattern`` are paired so the
 * user is blocked at INPUT time, not at submit. Submit button surfaces the
 * disabled reason inline.
 *
 * On 409 (``username_taken``), the dialog sets an inline error on the username
 * field; on 422 it falls back to a sonner toast via ``formatValidationError``.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { formatValidationError } from "@/lib/api";
import { TOKENS } from "@/lib/tokens";
import { ApiError } from "@/types/api";
import type { Lang, Role } from "@/types/api";

import { useCreateUser } from "./useUsers";

const ROLE_OPTIONS: Role[] = [
  "content_admin",
  "super_admin",
  "hr_manager",
  "hr_recruiter",
  "hiring_manager",
];
const LANG_OPTIONS: Lang[] = ["es", "en", "de"];

const schema = z.object({
  username: z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-zA-Z0-9._-]+$/),
  role: z.enum([
    "content_admin",
    "super_admin",
    "hr_manager",
    "hr_recruiter",
    "hiring_manager",
  ]),
  preferred_lang: z.enum(["es", "en", "de"]),
  initial_password: z.string().min(8).max(128),
});

type FormValues = z.infer<typeof schema>;

export function UserCreateDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const create = useCreateUser();

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      username: "",
      role: "hr_recruiter",
      preferred_lang: "es",
      initial_password: "",
    },
  });

  async function onSubmit(values: FormValues) {
    try {
      await create.mutateAsync(values);
      reset();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("username", {
          message: t("users:create_dialog.username_taken"),
        });
        return;
      }
      toast.error(formatValidationError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t("users:create_dialog.title")}
      description={t("users:create_dialog.description")}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="create-username">
            {t("users:create_dialog.username_label")}
          </Label>
          <Input
            id="create-username"
            type="text"
            autoComplete="off"
            required
            minLength={3}
            maxLength={64}
            pattern="[a-zA-Z0-9._-]+"
            aria-invalid={errors.username ? "true" : "false"}
            {...register("username")}
          />
          {errors.username && (
            <p className="text-sm mt-1" style={{ color: "#dc2626" }}>
              {errors.username.message ?? t("users:create_dialog.username_taken")}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="create-role">
            {t("users:create_dialog.role_label")}
          </Label>
          <select
            id="create-role"
            className="w-full rounded-md px-3"
            style={{
              minHeight: "44px",
              border: `1px solid ${TOKENS.borderStrong}`,
              backgroundColor: TOKENS.surfaceElevated,
              color: TOKENS.text,
            }}
            {...register("role")}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {t(`users:roles.${r}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="create-lang">
            {t("users:create_dialog.lang_label")}
          </Label>
          <select
            id="create-lang"
            className="w-full rounded-md px-3"
            style={{
              minHeight: "44px",
              border: `1px solid ${TOKENS.borderStrong}`,
              backgroundColor: TOKENS.surfaceElevated,
              color: TOKENS.text,
            }}
            {...register("preferred_lang")}
          >
            {LANG_OPTIONS.map((l) => (
              <option key={l} value={l}>
                {t(`users:langs.${l}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="create-password">
            {t("users:create_dialog.initial_password_label")}
          </Label>
          <PasswordInput
            id="create-password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={128}
            aria-invalid={errors.initial_password ? "true" : "false"}
            {...register("initial_password")}
          />
          {errors.initial_password && (
            <p className="text-sm mt-1" style={{ color: "#dc2626" }}>
              {t("users:create_dialog.password_min")}
            </p>
          )}
        </div>

        <div className="flex flex-row justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
            style={{ minHeight: "44px" }}
          >
            {t("users:actions.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={!isValid || isSubmitting || create.isPending}
            style={{
              backgroundColor: TOKENS.primary,
              color: TOKENS.primaryFg,
              minHeight: "44px",
              fontWeight: 600,
            }}
          >
            {create.isPending
              ? t("common:actions.loading")
              : t("users:create_dialog.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
